import gsap from 'gsap';
import { Application, Point, RENDERER_TYPE, Ticker } from 'pixi.js';

import { config, getActivityDataByHash, getFinalItemId, getObjectInfo, getPageDataByHash } from '../config/config';
import { objectConfig } from '../config/objectsConfig';
import { events } from './events';
import { ResizerDimensions, dimensions, resizeEmitter } from '../helpers/resizer';
import Input from '../helpers/Input';
import LayersContainer from './LayersContainer';
import InfoBubble from './InfoBubble';
import { isMobileOrTablet, isTouch } from '../helpers/features';
import Buttons from './Buttons';
import ActiveItem from './ActiveItem';
import Fader from './Fader';

import {
  enableMusic,
  initSound,
  playAmbient,
  playItemSfx,
  playItemSfxAfterTransformation,
  playMusic,
  playSfx,
  setMuted,
  stopItemSfx,
} from '../helpers/sounds';
import { getIframeTemplate } from '../helpers/tools';
import {
  EVENT_ACTIVITY_START,
  EVENT_HELP_SHOW,
  EVENT_HELP_HIDE,
  EVENT_OBJECT_DONE,
  EVENT_OPEN_INFO,
  sendCustomEvent,
  sendViewEvent,
  EVENT_MUSIC_SWITCH_OFF,
  EVENT_MUSIC_SWITCH_ON,
} from '../helpers/stats';

import * as PIXI from 'pixi.js';
import PixiPlugin from 'gsap/PixiPlugin';
import Item from './Item';
import ActiveObject from './ActiveObject';
import { ProcessedActivity } from './declarations/Activity';
import Layer from './Layer';
import ExtraObject from './ExtraObject';
import { Coordinates, Dimensions } from './declarations/PositionInfo';
import { isIOS } from 'mobile-device-detect';
import Counter from './Counter';
import Hint from './Hint';
import { ProcessedPage } from './declarations/Page';
PixiPlugin.registerPIXI(PIXI);
gsap.registerPlugin(PixiPlugin);

declare global {
  // eslint-disable-next-line no-var
  var __PIXI_APP__: Application;
}

declare let tingle: {
  modal: any;
};

export default class Scene {
  private items: { [key: string]: Item };
  private objects: { [key: string]: ActiveObject };
  private collectedObjectsCount: number;
  private objectsCount: number;
  private finalWindowWasOpened: boolean;
  private questWindow: HTMLElement | null;

  private input?: Input;
  private pixi?: Application;
  private layersContainer?: LayersContainer;
  private fader?: Fader;
  private infoBubble?: InfoBubble;
  private modal?: any;
  private touchedItem: Item | null = null;
  //private itemOverDelayedCall: any;
  private voiceover: any;
  private isDragging: boolean;
  private moveCounter: number;
  private counter?: Counter;
  private hint?: Hint;
  private hintPlayed: boolean = false;
  private objectCollectedDelayedCall?: GSAPTween;

  constructor() {
    this.isDragging = false;
    this.moveCounter = 0;
    this.items = {};
    this.objects = {};
    this.collectedObjectsCount = 0;
    this.objectsCount = Object.keys(objectConfig).length;
    this.finalWindowWasOpened = false;

    const questCloseButton = document.querySelector('.quest-close-button');
    this.questWindow = document.querySelector('.quest-complete');
    questCloseButton?.addEventListener('pointerup', () => this.onFinalWindowClosed());

    this.init();
  }

  init() {
    initSound();

    this.initPixi();
    this.createParts();

    this.hideUiEggsAndBasket();

    resizeEmitter.on(events.RESIZE, this.onResize, this);
    this.onResize(dimensions); //initial resize

    this.input = new Input();

    if (this.pixi) {
      this.input.init(this.pixi, isTouch);
    }

    this.initParallax();

    window.addEventListener('hashchange', () => {
      this.onHashChanged();
    });
    this.onHashChanged();
  }

  initPixi() {
    const rendererOptions = {
      // antialias: true,
      //backgroundAlpha: 0, // instead transparent

      antialias: true,
      backgroundAlpha: 0,
      resolution: window.devicePixelRatio || 1,
      view: document.querySelector('.canvas-container canvas') as HTMLCanvasElement,
      backgroundColor: 0xffffff,
      autoDensity: true,
    };
    this.pixi = new Application(rendererOptions);

    //for 1. initInteractivity(parallax pointermove / touchstart), 2. stage pointer up handler
    this.pixi.stage.eventMode = 'static'; // todo ???

    // for chrome PIXI plugin ------------------------
    globalThis.__PIXI_APP__ = this.pixi; //used by pixiJS Chrome plugin
    // globalThis.__PIXI_STAGE__ = this.pixi.stage;
    // globalThis.__PIXI_RENDERER__ = this.pixi.renderer;
    //------------------------------------------------

    console.log(
      `Pixi renderer type ${this.pixi.renderer.type === RENDERER_TYPE.WEBGL ? 'WEBGL' : 'CANVAS or UNKNOWN'}`,
    );
  }

  onResize(dimensions: ResizerDimensions) {
    if (this.pixi) {
      //Resizes the screen and canvas to the specified width and height. Canvas dimensions are multiplied by resolution.
      this.pixi.renderer.resize(dimensions.containerW, dimensions.containerH);
      this.pixi.stage.scale.set(dimensions.scale);

      //center stage
      this.pixi.stage.x = Math.round(dimensions.stageX);
      this.pixi.stage.y = Math.round(dimensions.stageY);

      this.hint?.onResize(dimensions);
      this.input?.onResize(dimensions);
    }
  }

  createParts() {
    //1. scene layers
    this.layersContainer = new LayersContainer();

    this.layersContainer.layers.forEach((layer) => {
      layer.items.forEach((item: Item) => {
        this.items[item.activeItem.id] = item;
      });
      layer.objects.forEach((activeObject: ActiveObject) => {
        this.objects[activeObject.id] = activeObject;
      });
    });

    this.fader = new Fader();
    this.counter = new Counter();
    this.counter.setText(this.collectedObjectsCount, this.objectsCount);
    this.hint = new Hint();

    if (this.pixi) {
      this.pixi.stage.addChild(this.layersContainer);
      this.pixi.stage.addChild(this.fader);
      this.pixi.stage.addChild(this.counter);
      this.pixi.stage.addChild(this.hint);
    }

    //2. activity bubble
    //const activityContainer = document.querySelector(isMobile ? '.mobile-activity-container' : '.activity-container');
    const activityContainer = document.querySelector<HTMLElement>('.activity-container');
    if (activityContainer) {
      this.infoBubble = new InfoBubble(
        activityContainer,
        () => this.onInfoBubbleShow(),
        () => this.onInfoBubbleHide(),
      );
    }

    //3. buttons
    const buttons = new Buttons();
    buttons.on(events.HELP_BUTTON_CLICK, (active) => this.onHelpButtonClick(active));
    buttons.on(events.MUSIC_BUTTON_CLICK, (active) => this.onMusicButtonClick(active));
  }

  initParallax() {
    const ticker = Ticker.shared;
    ticker.add(() => {
      const ratio = this.input?.getRatio();
      if (ratio) {
        this.layersContainer?.layers.forEach((layer) => layer.setPosition(ratio));
        this.layersContainer?.bulletLayers.forEach((bulletLayer) => bulletLayer.setPosition(ratio));
      }
    });
  }

  start() {
    playAmbient('atmo_loop');
    playMusic('background_music', true);

    this.showBullets(true);
    gsap.delayedCall(2, () => {
      this.showBullets(false);

      this.addItemListeners();
      this.addObjectListeners();

      this.input?.start();

      this.counter?.showCounter(true);
      this.hint?.showHint(true);
      playSfx('vo_napoveda');
      this.hintPlayed = true;
    });
    setTimeout(() => {
      this.hintPlayed = false;
    }, 15000);

    setTimeout(() => {
      this.counter?.showCounter(false);
    }, 7500);
  }

  showBullets(doShow = true) {
    let item;
    let index = 0;
    for (const itemName in this.items) {
      item = this.items[itemName];
      if (item.activeItem.interactive) {
        //not activeItem.isVisible because of show/hide item tween - could be visible but fading out
        gsap.delayedCall(
          0.05 * index,
          ((itm) => () => {
            itm.itemBullet.displayBullet(doShow);
          })(item),
        );
        index++;
      }
    }
  }

  //openModal(content: any, title: any, closeOnOverlay: any, modalClass: any) {
  openModal(content: any, closeOnOverlay: any, modalClass: any) {
    //console.log('openModal', content, title, closeOnOverlay, modalClass);
    setMuted(true);
    this.input?.stop();
    this.pixi?.stop();

    const closeMethods = closeOnOverlay ? ['button', 'escape', 'overlay'] : ['button', 'escape'];

    this.modal = new tingle.modal({
      footer: false,
      stickyFooter: false,
      closeMethods: closeMethods,
      closeLabel: '',
      cssClass: modalClass ? [modalClass] : [],
      //onOpen: () => this.onModalOpen(),
      onClose: () => this.onModalClose(),
    });

    this.modal.setContent(content);
    //this.modal.setFooterContent(title);
    this.modal.open();
  }

  // onModalOpen() {
  //   // setMuted(true);
  //   // this.input?.stop();
  //   // this.pixi?.stop();
  // }

  onModalClose() {
    this.modal.destroy();
    window.location.hash = '';
    setMuted(false);
    this.input?.start();
    this.pixi?.start();

    sendViewEvent();
  }

  onInfoBubbleShow() {
    this.input?.stop();
  }

  onInfoBubbleHide() {
    this.input?.start();
    if (isMobileOrTablet && this.touchedItem) {
      this.touchedItem.activeItem.setHoverState(false);
      this.touchedItem.itemBullet.setHoverState(false);
      this.touchedItem = null;
    }
    stopItemSfx();
  }

  onHashChanged() {
    let activity: ProcessedPage | ProcessedActivity | undefined;
    let closeOnOverlay = false;

    if (window.location.hash) {
      let hash = window.location.hash.substring(1);
      if (hash.indexOf(config.AZ_HASH) !== -1) {
        hash = config.AZ_HASH;
      }
      console.log(`HASH CHANGE: -${hash}-`);

      activity = getPageDataByHash(hash);
      if (!activity) {
        activity = getActivityDataByHash(hash);
        closeOnOverlay = false;
      }

      if (activity) {
        activity = activity as ProcessedActivity;
        if (isMobileOrTablet && activity.appLink) {
          //todo ??? trackEvent(ACTION_STORE_REDIRECT_CLICK, activity.linkHash);
          window.open(activity.appLink, '_blank');
        } else {
          //todo ??? trackEvent(ACTION_OPEN_POPUP, activity.linkHash);
          sendCustomEvent(EVENT_ACTIVITY_START, activity.linkHash);
          // this.openModal(getIframeTemplate(activity.url), activity.title, closeOnOverlay, activity.modalClass);
          if (activity && activity.url && activity.modalClass) {
            // stopItemSfx(activity.positionId, true);
            this.openModal(getIframeTemplate(activity.url), closeOnOverlay, activity.modalClass);
          }
        }
      } else {
        window.location.hash = '';
      }
    }
  }

  onStagePointerUp(event: Event) {
    if (isMobileOrTablet) {
      if (this.isDragging) {
        this.stopDragging();

        return;
      }
    }
    this.stopDragging(); // There seems to be a mysterious bug, this might solve it

    if (!(event.target instanceof ActiveItem)) {
      this.infoBubble?.hide();

      if (this.touchedItem) {
        this.touchedItem.activeItem.setHoverState(false);
        this.touchedItem.itemBullet.setHoverState(false);
        this.touchedItem = null;
      }
    }
  }

  addObjectListeners() {
    Object.values(this.objects).forEach((object) => {
      object.on('pointermove', () => this.onItemMove());
      object.on('pointerup', () => {
        if (isMobileOrTablet) {
          if (this.isDragging) {
            this.stopDragging();

            return;
          }
        }
        this.stopDragging(); // There seems to be a mysterious bug, this might solve it
        object.onPointerUp();
      });
      object.once(events.OBJECT_COLLECTED, (obj) => this.afterObjectCollected(obj.id));
      object.setActive();
    });
  }

  addItemListeners() {
    //hiding infoBuble on stage tap outside items - all platforms
    this.pixi?.stage.on('pointerup', (e) => this.onStagePointerUp(e));

    //item click / hover handlers
    Object.values(this.items).forEach((item) => {
      item.activeItem.on('pointerover', (e) => this.onItemOver(e));
      item.activeItem.on('pointerout', (e) => this.onItemOut(e));
      item.activeItem.on('pointerup', (e) => this.onItemRelease(e));
      item.activeItem.on('pointermove', () => this.onItemMove());
    });
  }

  private startDragging() {
    if (isMobileOrTablet) {
      this.isDragging = true;
    }
  }

  private stopDragging() {
    this.moveCounter = 0;
    if (isMobileOrTablet) {
      this.isDragging = false;
    }
  }

  onItemMove() {
    this.moveCounter++; // Android pointerMove is too sensitive
    if (!this.isDragging && (this.moveCounter > 20 || isIOS)) {
      this.startDragging();
    }
  }

  onItemOver(event: PIXI.FederatedPointerEvent) {
    if (event.global.x < 0 || event.global.x > 1600) {
      return;
    }
    const currentTarget = event.currentTarget as PIXI.FederatedEventTarget & { id: string };
    const id = currentTarget.id;
    const item = this.items[id];
    this.itemOverHandler(item);
  }

  //desktop - delay =  config.sound.ITEM_DELAY, mobile - delay = 0
  itemOverHandler(item: Item) {
    if (this.infoBubble?.isVisible) {
      return;
    }
    if (isMobileOrTablet) {
      return;
    }
    item.itemBullet.parent.addChild(item.itemBullet);
    item.activeItem.setHoverState(true);
    item.itemBullet.setHoverState(true);

    playItemSfx(item.id, null); //finish callback in case of mobile
  }

  onItemOut(event: Event) {
    const currentTarget = event.currentTarget as EventTarget & { id: string };
    const id = currentTarget.id;
    const item = this.items[id];
    this.itemOutHandler(item);
  }

  itemOutHandler(item: Item) {
    if (isMobileOrTablet) return;
    //clear gsap delayed call started in itemOverHandler (desktop)
    //if (this.itemOverDelayedCall) {
    //  this.itemOverDelayedCall.kill();
    //}

    item.activeItem.setHoverState(false);
    item.itemBullet.setHoverState(false);

    if (!this.infoBubble?.isVisible) {
      stopItemSfx();
    }
  }

  onItemRelease(event: Event) {
    if (isMobileOrTablet) {
      if (this.isDragging) {
        this.stopDragging();

        return;
      }
    }
    this.stopDragging(); // There seems to be a mysterious bug, this might solve it

    const currentTarget = event.currentTarget as EventTarget & { id: string };
    const id = currentTarget.id;
    const item = this.items[id];

    if (isMobileOrTablet) {
      if (this.touchedItem) {
        this.touchedItem.activeItem.setHoverState(false);
        this.touchedItem.itemBullet.setHoverState(false);
        this.touchedItem = null;
      }
      this.touchedItem = item;
      this.touchedItem.activeItem.setHoverState(true);
      this.openItemInfoBubble(item);
      playItemSfx(item.id, null);
    } else {
      //desktop
      this.openItemInfoBubble(item);
    }
  }

  openItemInfoBubble(item: Item) {
    if (!isMobileOrTablet) {
      // stopItemSfx(item.id, true);
    }

    if (this.infoBubble) {
      const bulletPosition = this.fixInfoBubblePosition(
        item.itemBullet.toGlobal(new Point(0, 0)),
        this.infoBubble.getSize(),
      );
      this.infoBubble?.show(item.activity, bulletPosition);
    }

    sendCustomEvent(EVENT_OPEN_INFO, item.activity.linkHash);
  }

  afterObjectCollected(objectId: string) {
    stopItemSfx(true);
    this.collectedObjectsCount++;
    this.counter?.setText(this.collectedObjectsCount, this.objectsCount);
    this.counter?.blink();

    sendCustomEvent(EVENT_OBJECT_DONE, this.collectedObjectsCount);

    const allCollected = this.collectedObjectsCount === this.objectsCount;

    const objectInfo = getObjectInfo(objectId);
    //const item = this.items[objectInfo.item];

    const finalItem = this.items[getFinalItemId(objectInfo.item)];
    const layer = finalItem.activeItem.parent as Layer;
    const newStageRatio = layer.getStageRatio(finalItem.activeItem.center);

    finalItem.activeItem.setHoverState(true);

    if (this.objectCollectedDelayedCall) {
      this.objectCollectedDelayedCall.kill();
    }
    this.input?.tweenToRatio(newStageRatio, 0.5, 1); //todo startDelay / duration
    this.objectCollectedDelayedCall = gsap.delayedCall(1.5, () => {
      const transformationSound = playSfx('transformation', 0.7);
      transformationSound.on('end', () => {
        const itemSfx = playItemSfxAfterTransformation(finalItem.activeItem.id, () => {
          finalItem.activeItem.setHoverState(false);
          if (allCollected) {
            gsap.delayedCall(2, () => this.afterAllObjectsCollected());
          }
        });
        itemSfx.on('stop', () => {
          finalItem.activeItem.setHoverState(false);
        });
      });
      const faderPosition = finalItem.activeItem.toGlobal(
        new Point(finalItem.activeItem.width / 2, finalItem.activeItem.height / 2),
      );
      faderPosition.x = faderPosition.x / dimensions.scale;
      faderPosition.y = faderPosition.y / dimensions.scale;

      this.fader?.fadeInOut(faderPosition);
      this.items[objectInfo.item].activeItem.show(false, false);
      this.items[objectInfo.item].activeItem.setHoverState(false);
      this.items[objectInfo.item].itemBullet.setHoverState(false);

      this.items[finalItem.id].activeItem.show(true, false);
    });
    this.objectCollectedDelayedCall?.eventCallback('onInterrupt', () => {
      this.items[objectInfo.item].activeItem.show(false, false);
      this.items[objectInfo.item].activeItem.setHoverState(false);
      this.items[objectInfo.item].itemBullet.setHoverState(false);
      finalItem.activeItem.show(true, false);
      finalItem.activeItem.setHoverState(false);
      if (allCollected) {
        gsap.delayedCall(2, () => this.afterAllObjectsCollected());
      }
    });
  }

  hideUiEggsAndBasket() {
    const basketLayer = this.layersContainer?.layers[4];
    const basket = basketLayer?.extra['kosik'];
    if (basket) {
      basket.visible = false;
    }

    const paintingLayer = this.layersContainer?.layers[5];
    Object.keys(objectConfig).forEach((objectId) => {
      const counterExtraObject = paintingLayer?.extra['counter_' + objectId];
      if (counterExtraObject) {
        counterExtraObject.visible = false;
      }
    });
  }

  afterAllObjectsCollected() {
    stopItemSfx(true);
    const allCollectedSound = playSfx('all-collected', 0.7);
    allCollectedSound.on('end', () => {
      playSfx('teta_outro_01');
    });

    const tetaLayer = this.layersContainer?.layers[5];
    const teta = tetaLayer?.extra['teta'] as ExtraObject;
    let newStageRatio;
    if (teta) {
      newStageRatio = tetaLayer?.getStageRatio(teta.center || { x: 0, y: 0 });
      if (newStageRatio) {
        this.input?.tweenToRatio(newStageRatio, 1, 0);
      }

      teta.visible = true;
      const faderPosition = teta.toGlobal(new Point(teta.width / 2, teta.height / 2));
      faderPosition.x = faderPosition.x / dimensions.scale;
      faderPosition.y = faderPosition.y / dimensions.scale;
      this.fader?.fadeInOut(faderPosition);
    }

    gsap.to(teta.animSprite, {
      delay: 1 + 1 + 2,
      duration: 2,
      //x: '-180',
      onStart: () => {
        // playSfx('cave');
      },
      onComplete: () => {
        teta.setActive();
        teta.on('pointerup', () => this.openFinalWindow());
        gsap.delayedCall(1, () => this.openFinalWindow());
      },
    });
  }

  openFinalWindow() {
    if (!this.finalWindowWasOpened) {
      this.voiceover = playSfx('teta_outro_02');
    }
    this.pixi?.stop();
    this.input?.stop();
    if (this.pixi) {
      this.pixi.stage.eventMode = 'none';
    }
    this.questWindow?.classList.remove('hidden');
  }

  onFinalWindowClosed() {
    if (!this.finalWindowWasOpened) {
      if (this.voiceover) {
        this.voiceover.stop();
        this.voiceover = playSfx('teta_after_outro');
      }
    }
    if (this.pixi) {
      this.pixi.stage.eventMode = 'static';
    }
    this.finalWindowWasOpened = true;
    this.questWindow?.classList.add('hidden');
    this.pixi?.start();
    this.input?.start();
  }

  fixInfoBubblePosition(centerPosition: Coordinates, bubbleSize: Dimensions) {
    const pos = centerPosition;
    if (pos.x < bubbleSize.w / 2) {
      pos.x = bubbleSize.w / 2;
    } else if (pos.x > dimensions.containerW - bubbleSize.w / 2) {
      pos.x = dimensions.containerW - bubbleSize.w / 2;
    }

    if (pos.y < bubbleSize.h / 2) {
      pos.y = bubbleSize.h / 2;
    } else if (pos.y > dimensions.containerH - bubbleSize.h / 2) {
      pos.y = dimensions.containerH - bubbleSize.h / 2;
    }
    return pos;
  }

  onHelpButtonClick(active: boolean) {
    this.showBullets(active);
    if (active) {
      this.hint?.showHint(active);
      sendCustomEvent(EVENT_HELP_SHOW);
      if (this.hintPlayed === false && active) {
        stopItemSfx(true);
        playSfx('vo_napoveda');
        this.hintPlayed = true;
      }
      setTimeout(() => {
        this.hintPlayed = false;
      }, 15000);
    } else {
      sendCustomEvent(EVENT_HELP_HIDE);
    }
    this.counter?.showCounter(active);
    //todo ??? trackEvent(ACTION_BUTTON, active ? LABEL_HELP_ON : LABEL_HELP_OFF);
  }

  onMusicButtonClick(active: boolean) {
    //todo ??? trackEvent(ACTION_BUTTON, !active ? LABEL_MUSIC_ON : LABEL_MUSIC_OFF);
    if (active) {
      sendCustomEvent(EVENT_MUSIC_SWITCH_OFF);
    } else {
      sendCustomEvent(EVENT_MUSIC_SWITCH_ON);
    }
    enableMusic(!active);
  }
}
